/**
 * CR-6347: Reworked PO Lines filter panel for the Customer View.
 *
 * Mirrors the supplier sectioned panel (CR-6348): filters are grouped into
 * labelled sections — Date Range / Company / Supplier / Classification / Order
 * Details — and the legacy ODD Status + OTD Status to LSPD filters are exposed.
 * Per the supplied diagram the filter header stays visible when the sections are
 * collapsed and carries the Refresh / Export / Hide-filters controls, while the
 * footer holds Clear All / Apply Filters.
 *
 * Existing behaviour is preserved: input types are unchanged, the organisation
 * hierarchy view-scoping (viewScopedCustomers) still narrows the Company Site
 * list, the Division -> Site -> Warehouse and Supplier Group -> Site cascades
 * carry over via the shared hook, and the schedule feature-flag gating remains.
 *
 * CR-6348 rework: Buyer ID, OTIF Overridden, and PPV filters are now visible to
 * all users including suppliers (per client diagram).
 *
 * CR-6347 rework (client test feedback): adds the legacy Supplier Type and
 * Strategic-suppliers-only filters, shows Order Type unconditionally (driven
 * by the ERP order-type codes present in scope rather than a feature flag),
 * and the Company Site -> Supplier Site cascade now narrows via the shared
 * hook.
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Search, X, Calendar, RefreshCw, Download, Filter, Building2, Truck, Tags, FileText } from 'lucide-react';
import {
  POLinesFilters,
  FilterOptions,
  erpOrderTypeOptions,
} from '../types';
import { useView } from '../../../../../contexts/ViewContext';
import { useMenuSettings } from '../../../../../contexts/MenuSettingsContext';
import { usePortalMode } from '../../../../../hooks/usePortalMode';
import { FIELD_CLS, LABEL_CLS, FilterSection } from './fieldStyles';
import { CompanySection } from './sections/CompanySection';
import { SupplierSection } from './sections/SupplierSection';
import { ClassificationSection } from './sections/ClassificationSection';
import { OrderDetailsSection } from './sections/OrderDetailsSection';
import { usePOLinesFilterCascades } from './usePOLinesFilterCascades';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface CustomerSectionedFiltersProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  // CR-6347: the header persists even when the sections are collapsed, so the
  // panel owns the show/hide toggle plus the Refresh + Export controls the
  // diagram moved out of the results toolbar.
  showFilters: boolean;
  onToggleFilters: () => void;
  onRefresh: () => void;
  onExport: (format: 'xlsx' | 'csv') => void;
  exporting: boolean;
}

// Search-helper keys excluded from the "active" count.
const NON_COUNTED_KEYS = new Set(['supplierSearch', 'customerSearch']);

export function CustomerSectionedFilters({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  showFilters,
  onToggleFilters,
  onRefresh,
  onExport,
  exporting,
}: CustomerSectionedFiltersProps) {
  const { isFeatureEnabled } = useMenuSettings();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');

  // CR-4577: narrow the Company Site list to the active view context.
  const { viewLevel, selectedCompanyId, selectedGroupId } = useView();
  const { isSupplier } = usePortalMode();
  const viewScopedCustomers = useMemo(() => {
    const all = filterOptions?.customers || [];
    if (isSupplier) return all;
    if (viewLevel === 'SITE' && selectedCompanyId) return all.filter((c) => c.id === selectedCompanyId);
    if (viewLevel === 'COMPANY' && selectedCompanyId) return all.filter((c) => c.id === selectedCompanyId);
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const inGroup = all.filter((c) => c.group === selectedGroupId);
      return inGroup.length > 0 ? inGroup : all;
    }
    return all;
  }, [filterOptions?.customers, viewLevel, selectedCompanyId, selectedGroupId, isSupplier]);

  const divisions = filterOptions?.divisions || [];
  const supplierGroups = filterOptions?.supplier_groups || [];
  const siteCountries = filterOptions?.site_countries || [];
  const supplierCountries = filterOptions?.supplier_countries || [];
  const commodityGroups = filterOptions?.commodity_groups || [];
  const commodityTypes = filterOptions?.commodity_types || [];
  // CR-6347 rework: Order Type = the ERP order-type codes present in scope.
  const orderTypeOptions = useMemo(
    () => erpOrderTypeOptions(filterOptions?.erp_order_types),
    [filterOptions?.erp_order_types],
  );
  // CR-7400: Buyer ID = the ERP buyer codes present on the lines in scope.
  const buyerIdOptions = filterOptions?.buyer_ids || [];

  // Shared dependent-filter cascades, seeded with the view-scoped company list.
  const {
    companySiteOptions,
    warehouseOptions,
    supplierSiteOptions,
    handleCompanyCountryChange,
    handleDivisionChange,
    handleCompanySiteChange,
    handleSupplierGroupChange,
    handleSupplierCountryChange,
  } = usePOLinesFilterCascades(filters, filterOptions, onFilterChange, viewScopedCustomers);

  const handleCommodityGroupChange = (value: string) => {
    onFilterChange('commodityGroup', value);
    if (value !== filters.commodityGroup) {
      onFilterChange('commodityType', '');
    }
  };

  const activeCount = useMemo(
    () =>
      Object.entries(filters).filter(
        ([k, v]) => !NON_COUNTED_KEYS.has(k) && v !== '' && v != null,
      ).length,
    [filters],
  );

  // Order Type + On Hold live in Order Details per the diagram but are
  // customer-only (Order Type) / feature-flag gated (On Hold), so they are
  // injected rather than baked into the shared section component.
  //
  // CR-6347 rework: Order Type is the legacy filter the client asked for. It
  // reads PurchaseOrder.erp_order_type (the column the ERP feed writes; the
  // dormant `order_type` column is deliberately not offered — CR-7273) and
  // is always shown; its options come from the codes actually in scope.
  const orderDetailExtras = (
    <>
      <div>
        <label className={LABEL_CLS}>Order Type</label>
        <select
          value={filters.erpOrderType}
          onChange={(e) => onFilterChange('erpOrderType', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Order Types</option>
          {orderTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      {!scheduleCollabEnabled && (
        <div>
          <label className={LABEL_CLS}>On Hold</label>
          <select
            value={filters.on_hold}
            onChange={(e) => onFilterChange('on_hold', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">All</option>
            <option value="yes">On Hold</option>
            <option value="no">Not On Hold</option>
          </select>
        </div>
      )}
    </>
  );

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl">Filter PO Lines</CardTitle>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onExport('xlsx')}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <button
              type="button"
              onClick={onToggleFilters}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                showFilters
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  showFilters
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {showFilters && (
        <CardContent className="p-0">
          <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="space-y-0">
            {/* CR-6347: Sections with shaded headers, icons and dividers matching Pricebooks layout */}
            <div className="[&>section]:border-t-0">
              <SectionDivider />

              {/* Date Range */}
              <FilterSection title="Date Range" icon={Calendar}>
                <div>
                  <label className={LABEL_CLS}>ODD From</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="date"
                      value={filters.oddFrom}
                      onChange={(e) => onFilterChange('oddFrom', e.target.value)}
                      className={`${FIELD_CLS} pl-9`}
                    />
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLS}>ODD To</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="date"
                      value={filters.oddTo}
                      onChange={(e) => onFilterChange('oddTo', e.target.value)}
                      className={`${FIELD_CLS} pl-9`}
                    />
                  </div>
                </div>
              </FilterSection>

              <SectionDivider />

              <CompanySection
                filters={filters}
                onFilterChange={onFilterChange}
                siteCountries={siteCountries}
                divisions={divisions}
                companySiteOptions={companySiteOptions}
                warehouseOptions={warehouseOptions}
                onCompanyCountryChange={handleCompanyCountryChange}
                onDivisionChange={handleDivisionChange}
                onCompanySiteChange={handleCompanySiteChange}
                icon={Building2}
              />

              <SectionDivider />

              <SupplierSection
                filters={filters}
                onFilterChange={onFilterChange}
                supplierCountries={supplierCountries}
                supplierGroups={supplierGroups}
                supplierSiteOptions={supplierSiteOptions}
                onSupplierGroupChange={handleSupplierGroupChange}
                onSupplierCountryChange={handleSupplierCountryChange}
                icon={Truck}
                showStrategic
                showSupplierType
              />

              <SectionDivider />

              <ClassificationSection
                filters={filters}
                onFilterChange={onFilterChange}
                commodityGroups={commodityGroups}
                commodityTypes={commodityTypes}
                onCommodityGroupChange={handleCommodityGroupChange}
                icon={Tags}
              />

              <SectionDivider />

              <OrderDetailsSection
                filters={filters}
                onFilterChange={onFilterChange}
                showPartAndDescription={false}
                extraFields={orderDetailExtras}
                icon={FileText}
                buyerIdOptions={buyerIdOptions}
              />
            </div>

            {/* Footer actions - CR-6347: Clear All left, Apply Filters right */}
            <div className="flex items-center justify-between px-6 py-4">
              <Button type="button" variant="outline" onClick={onClearFilters} className="px-6">
                <X className="h-4 w-4 mr-2" />
                Clear All
              </Button>
              <Button type="submit" className="px-6">
                <Search className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </form>
        </CardContent>
      )}
    </Card>
  );
}
